import { useFormContext, useWatch } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@vnetwork/ui/primitives';
import type { ContractFormValues } from '../contract-form';
import { NumericInput } from '@/components/numeric-input';

const UNLIMITED = 999999999;

interface PriceRange {
  price_start?: number | string;
  price_end?: number | string;
  price_per_unit?: number | string;
}

/**
 * Tiered/progressive price-range editor bound to an RHF array path (e.g.
 * `products.0.service_configoptions.0.prices`). Mirrors legacy `PriceRangesList`:
 * the first tier starts at 0 (start locked), the last tier ends at "unlimited",
 * and adding a tier splits the previous open-ended tier. Ranges below the
 * `commitmentValue` floor are hidden (already covered by the commitment).
 */
export function PriceRangesList({
  name,
  commitmentValue = 0,
}: {
  name: string;
  commitmentValue?: number;
}) {
  const { control, setValue } = useFormContext<ContractFormValues>();
  const ranges =
    (useWatch({ control, name: name as never }) as PriceRange[] | undefined) ?? [];

  const commit = (next: PriceRange[]) =>
    setValue(name as never, next as never, { shouldDirty: true, shouldTouch: true });

  const addRange = () => {
    const next = [...ranges];
    if (next.length === 0) {
      next.push({ price_start: 0, price_end: UNLIMITED, price_per_unit: 0 });
    } else {
      const last = next[next.length - 1];
      const lastStart = Number(last.price_start) || 0;
      const splitEnd = lastStart + Math.max(1000, lastStart * 0.5);
      next[next.length - 1] = { ...last, price_end: splitEnd };
      next.push({ price_start: splitEnd, price_end: UNLIMITED, price_per_unit: last.price_per_unit ?? 0 });
    }
    commit(next);
  };

  const removeLast = () => {
    if (ranges.length === 0) return;
    const next = ranges.slice(0, -1);
    if (next.length > 0) next[next.length - 1] = { ...next[next.length - 1], price_end: UNLIMITED };
    commit(next);
  };

  const update = (i: number, field: keyof PriceRange, value: number) => {
    const next = ranges.map((r, idx) => (idx === i ? { ...r, [field]: value } : r));
    commit(next);
  };

  const visibleCount = ranges.filter((r) => Number(r.price_end) > commitmentValue).length;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 'var(--vn-space-3)',
          fontSize: 'var(--vn-font-size-xs)',
          color: 'var(--vn-fg-muted)',
        }}
      >
        <span>Usage from</span>
        <span>Usage to</span>
        <span>Price per unit</span>
      </div>

      {ranges.length === 0 && (
        <div style={{ padding: 'var(--vn-space-4)', textAlign: 'center', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
          No pricing ranges yet.
        </div>
      )}

      {ranges.map((range, i) => {
        if (Number(range.price_end) <= commitmentValue) return null;
        return (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--vn-space-3)' }}>
            <NumericInput
              aria-label={`Range ${i + 1} start`}
              value={String(range.price_start ?? '')}
              disabled={i === 0}
              onChange={(e) => update(i, 'price_start', Number(e.target.value))}
            />
            <NumericInput
              aria-label={`Range ${i + 1} end`}
              value={String(range.price_end ?? '')}
              onChange={(e) => update(i, 'price_end', Number(e.target.value))}
            />
            <NumericInput
              aria-label={`Range ${i + 1} price per unit`}
              value={String(range.price_per_unit ?? '')}
              onChange={(e) => update(i, 'price_per_unit', Number(e.target.value))}
            />
          </div>
        );
      })}

      <div style={{ display: 'flex', gap: 'var(--vn-space-2)', justifyContent: 'flex-end' }}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          startIcon={<Trash2 size={14} />}
          disabled={visibleCount <= 1}
          onClick={removeLast}
        >
          Remove range
        </Button>
        <Button type="button" variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={addRange}>
          Add range
        </Button>
      </div>
    </div>
  );
}
